import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { LoadingSpinner } from '../LoadingSpinner';
import { formatStreakMessage } from '../../utils/format';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

interface StreakData {
  currentStreak: number;
  longestStreak: number;
  totalDaysListened: number;
  streakHistory: Array<{
    date: string;
    hasListened: boolean;
    songsPlayed: number;
    timeListened: number;
  }>;
  achievements: Array<{
    id: string;
    name: string;
    description: string;
    unlockedAt: string;
    icon: string;
  }>;
}

export function ListeningStreaks() {
  const { user } = useAuth();

  const { data: streakData, isLoading, error } = useQuery<StreakData>({
    queryKey: ['analytics', 'streaks'],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/analytics/streaks', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch streak data');
      }
      
      return response.json();
    },
    enabled: !!user,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !streakData) {
    return (
      <div className="text-center py-8">
        <p className="text-slate-grey">Unable to load streak data</p>
      </div>
    );
  }

  const getStreakColor = (streak: number) => {
    if (streak === 0) return 'text-slate-grey';
    if (streak < 7) return 'text-cyber-teal';
    if (streak < 30) return 'text-electric-blue';
    if (streak < 100) return 'text-soft-violet';
    return 'text-naia-pink';
  };

  const getStreakIcon = (streak: number) => {
    if (streak === 0) return <CalendarTodayIcon className="text-slate-grey" />;
    if (streak < 7) return <TrendingUpIcon className="text-cyber-teal" />;
    if (streak < 30) return <LocalFireDepartmentIcon className="text-electric-blue" />;
    if (streak < 100) return <LocalFireDepartmentIcon className="text-soft-violet" />;
    return <EmojiEventsIcon className="text-naia-pink" />;
  };

  // Get last 14 days for streak visualization
  const getLast14Days = () => {
    const days = [];
    const today = new Date();
    
    for (let i = 13; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const historyEntry = streakData.streakHistory.find(
        entry => entry.date.split('T')[0] === dateStr
      );
      
      days.push({
        date: dateStr,
        hasListened: historyEntry?.hasListened || false,
        songsPlayed: historyEntry?.songsPlayed || 0,
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNumber: date.getDate()
      });
    }
    
    return days;
  };

  const last14Days = getLast14Days();

  return (
    <div className="space-y-6">
      {/* Current Streak Display */}
      <div className="text-center">
        <div className="flex items-center justify-center mb-3">
          {getStreakIcon(streakData.currentStreak)}
          <span className={`text-3xl font-bold ml-2 ${getStreakColor(streakData.currentStreak)}`}>
            {streakData.currentStreak}
          </span>
        </div>
        <p className="text-pearl-white font-semibold text-lg">
          {streakData.currentStreak === 1 ? 'Day' : 'Days'}
        </p>
        <p className="text-slate-grey text-sm mt-1">
          {formatStreakMessage(streakData.currentStreak)}
        </p>
      </div>

      {/* Streak Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="text-center p-3 bg-dark-void/50 rounded-lg border border-white/10">
          <p className="text-2xl font-bold text-electric-blue">
            {streakData.longestStreak}
          </p>
          <p className="text-slate-grey text-xs">Longest Streak</p>
        </div>
        <div className="text-center p-3 bg-dark-void/50 rounded-lg border border-white/10">
          <p className="text-2xl font-bold text-soft-violet">
            {streakData.totalDaysListened}
          </p>
          <p className="text-slate-grey text-xs">Total Days</p>
        </div>
      </div>

      {/* 14-Day Calendar View */}
      <div>
        <h4 className="text-pearl-white font-semibold text-sm mb-3">Last 14 Days</h4>
        <div className="grid grid-cols-7 gap-1">
          {last14Days.map((day, index) => (
            <div
              key={day.date}
              className="aspect-square relative group cursor-pointer"
              title={`${day.dayName} ${day.dayNumber}: ${day.hasListened ? `${day.songsPlayed} songs` : 'No activity'}`}
            >
              <div className={`
                w-full h-full rounded-md border-2 transition-all duration-200
                ${day.hasListened 
                  ? 'bg-gradient-to-br from-naia-pink/30 to-soft-violet/30 border-naia-pink/50' 
                  : 'bg-dark-void/30 border-white/10'
                }
                group-hover:scale-110 group-hover:z-10
              `}>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={`text-xs font-medium ${
                    day.hasListened ? 'text-pearl-white' : 'text-slate-grey'
                  }`}>
                    {day.dayNumber}
                  </span>
                </div>
                {day.hasListened && (
                  <div className="absolute -top-1 -right-1">
                    <div className="w-2 h-2 bg-naia-pink rounded-full"></div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-between text-xs text-slate-grey mt-2">
          <span>2 weeks ago</span>
          <span>Today</span>
        </div>
      </div>

      {/* Recent Achievements */}
      {streakData.achievements && streakData.achievements.length > 0 && (
        <div>
          <h4 className="text-pearl-white font-semibold text-sm mb-3">Recent Achievements</h4>
          <div className="space-y-2">
            {streakData.achievements.slice(0, 2).map((achievement) => (
              <div
                key={achievement.id}
                className="flex items-center gap-3 p-3 bg-gradient-to-r from-soft-violet/20 to-electric-blue/20 rounded-lg border border-soft-violet/30"
              >
                <div className="text-xl">{achievement.icon}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-pearl-white font-semibold text-sm truncate">
                    {achievement.name}
                  </p>
                  <p className="text-slate-grey text-xs truncate">
                    {achievement.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Motivation Section */}
      <div className="text-center p-4 bg-gradient-to-r from-naia-pink/10 to-soft-violet/10 rounded-lg border border-naia-pink/20">
        <p className="text-pearl-white text-sm font-medium">
          {streakData.currentStreak === 0 
            ? "Listen to a song today to start your streak!" 
            : streakData.currentStreak < 7
            ? "Keep listening daily to build your streak!"
            : "You're on fire! Keep the momentum going!"
          }
        </p>
      </div>
    </div>
  );
} 